import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";

import { get } from "lib/api";
import { useAuth } from "context/AuthContext";
import PropTypes from "prop-types";
import { IosCard, IosList, IosListItem, IosPage, IosSectionHeader, iosColors } from "mobile/components/IosParity";
import MobileInstallPrompt from "mobile/components/MobileInstallPrompt";

const useCount = (key, path, params) =>
  useQuery({
    queryKey: key,
    queryFn: async () => {
      const res = await get(path, { ...(params || {}), pagination: { page: 1, pageSize: 1 } });
      return res?.meta?.pagination?.total ?? 0;
    },
    staleTime: 10_000,
  });

const StatCard = ({ title, value, icon, color = "primary" }) => (
  <IosCard sx={{ p: 1.6, minHeight: 98, boxShadow: "none" }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.75}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: iosColors.muted, fontSize: 12, fontWeight: 750, lineHeight: 1.15 }}>
          {title}
        </Typography>
        <Typography sx={{ color: iosColors.ink, fontSize: 23, fontWeight: 900, mt: 0.55, lineHeight: 1 }}>
          {value}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 1,
          display: "grid",
          placeItems: "center",
          bgcolor: color === "primary" ? "rgba(215,38,30,0.1)" : "rgba(255,106,0,0.12)",
          color: color === "primary" ? iosColors.red : iosColors.orange,
          flexShrink: 0,
        }}
      >
        <Icon fontSize="small">{icon}</Icon>
      </Box>
    </Stack>
  </IosCard>
);

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  icon: PropTypes.string.isRequired,
  color: PropTypes.oneOf(["primary", "secondary"]),
};

export default function MobileDashboard() {
  const navigate = useNavigate();
  const { user, pluginRole, activeCompany, hasPermission } = useAuth();

  const totalWorkOrders = useCount(["m-dash", "wo", "total"], "/api/work-orders");
  const openWorkOrders = useCount(["m-dash", "wo", "open"], "/api/work-orders", {
    filters: { status: { $ne: "complete" } },
  });
  const inProgress = useCount(["m-dash", "pipeline", "in_progress"], "/api/stage-records", {
    filters: { status: { $eq: "in_progress" } },
  });
  const inventoryItems = useCount(["m-dash", "inv", "total"], "/api/inventory-items");

  const greeting = useMemo(() => {
    const name =
      user?.username ||
      user?.email ||
      user?.name ||
      user?.attributes?.username ||
      user?.attributes?.email ||
      null;
    return name ? `Welcome, ${String(name).split("@")[0]}` : "Welcome back";
  }, [user]);
  const roleLabel = String(pluginRole || "").trim() || "authenticated";
  const companyLabel = String(activeCompany || "").trim().toUpperCase();
  const actions = useMemo(
    () =>
      [
        { group: "Start", title: "Find", subtitle: "Search records", icon: "search", to: "/m/search" },
        { group: "Production", title: "Pipeline", subtitle: "Run builds", icon: "view_kanban", to: "/m/pipeline", permissionKey: "pipeline" },
        { group: "Production", title: "Work Orders", subtitle: "Search + status", icon: "build", to: "/m/work-orders", permissionKey: "work-orders" },
        { group: "Inventory", title: "Inventory", subtitle: "Lookup SKU", icon: "inventory_2", to: "/m/inventory", permissionKey: "inventory" },
        { group: "Inventory", title: "Valve Bodies", subtitle: "QR + lifecycle", icon: "qr_code_2", to: "/m/valve-bodies", permissionKey: "valve-bodies" },
        { group: "Sales", title: "Sales", subtitle: "Orders + pull flow", icon: "receipt_long", to: "/m/sales", permissionKey: "sales" },
        { group: "Sales", title: "Order Pulls", subtitle: "Pick + verify", icon: "inventory_2", to: "/m/pull-orders", permissionKey: "sales" },
        { group: "Sales", title: "Core Returns", subtitle: "Match returns", icon: "assignment_return", to: "/m/core-returns", permissionKey: "sales" },
        { group: "Sales", title: "Warranty", subtitle: "Claims + chat", icon: "verified_user", to: "/m/sales/warranty-claims", permissionKey: "sales" },
        { group: "Purchasing", title: "Receiving", subtitle: "Photos + counts", icon: "move_to_inbox", to: "/m/warehouse/receiving", permissionKey: "warehouse-receiving" },
        { group: "Purchasing", title: "Purchase", subtitle: "PO tracking", icon: "shopping_cart", to: "/m/purchase-orders", permissionKey: "purchase-orders" },
        { group: "Accounts", title: "Customers", subtitle: "Accounts", icon: "people", to: "/m/customers", permissionKey: "customers" },
        { group: "Accounts", title: "Vendors", subtitle: "Suppliers", icon: "store", to: "/m/vendors", permissionKey: "vendors" },
        { group: "Accounts", title: "Invoices", subtitle: "Billing", icon: "request_quote", to: "/m/invoices", permissionKey: "invoices" },
        { group: "Accounts", title: "Payments", subtitle: "Receipts", icon: "payments", to: "/m/payments", permissionKey: "payments" },
        { group: "Tools", title: "Projections", subtitle: "Forecasts", icon: "insights", to: "/m/projections", permissionKey: "projections" },
        { group: "Tools", title: "Gallery", subtitle: "Images", icon: "photo_library", to: "/m/media/gallery", permissionKey: "image-gallery" },
        { group: "Tools", title: "Settings", subtitle: "Profile + sign out", icon: "settings", to: "/m/settings" },
      ].filter((action) => !action.permissionKey || hasPermission(action.permissionKey)),
    [hasPermission]
  );
  const groupedActions = useMemo(
    () =>
      actions.reduce((acc, action) => {
        if (!acc[action.group]) acc[action.group] = [];
        acc[action.group].push(action);
        return acc;
      }, {}),
    [actions]
  );

  return (
    <IosPage>
      <Stack spacing={1.4}>
        <IosSectionHeader
          eyebrow={companyLabel || "VBS"}
          title={greeting}
          subtitle={companyLabel ? `${roleLabel} • ${companyLabel}` : roleLabel}
          action={<Chip size="small" label="Today" sx={{ bgcolor: "#fff", border: `1px solid ${iosColors.line}`, fontWeight: 900 }} />}
        />

        <MobileInstallPrompt />

        <Grid container spacing={0.75}>
          <Grid item xs={6}>
            <StatCard
              title="Open work orders"
              value={openWorkOrders.data ?? "—"}
              icon="build"
              color="primary"
            />
          </Grid>
          <Grid item xs={6}>
            <StatCard
              title="Pipeline in progress"
              value={inProgress.data ?? "—"}
              icon="view_kanban"
              color="secondary"
            />
          </Grid>
          <Grid item xs={6}>
            <StatCard
              title="Total work orders"
              value={totalWorkOrders.data ?? "—"}
              icon="insights"
              color="secondary"
            />
          </Grid>
          <Grid item xs={6}>
            <StatCard
              title="Inventory items"
              value={inventoryItems.data ?? "—"}
              icon="inventory_2"
              color="primary"
            />
          </Grid>
        </Grid>

        <IosSectionHeader eyebrow="Workflows" title="Open a workflow" subtitle="Grouped for quick one-hand scanning." />
        {Object.entries(groupedActions).map(([group, items]) => (
          <Box key={group}>
            <Typography sx={{ color: iosColors.muted, fontSize: 11, fontWeight: 900, textTransform: "uppercase", mb: 0.55, px: 0.25 }}>
              {group}
            </Typography>
            <IosList>
              {items.map((a) => (
                <IosListItem
                  key={a.title}
                  icon={a.icon}
                  title={a.title}
                  meta={a.subtitle}
                  accent={a.accent || "orange"}
                  onClick={() => navigate(a.to)}
                />
              ))}
            </IosList>
          </Box>
        ))}
      </Stack>
    </IosPage>
  );
}
